import { AnalyticsService } from 'base/analytics';
import { UnreachableError } from 'base/helpers';
import { Button } from 'base/ui/button';
import { IconPage, IconPageGroup } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { PanelLayout } from 'base/ui/panel';
import { TextRegular } from 'base/ui/typography';
import { PageEditingActions } from 'blocks/editor/editing_actions/page';
import { Page } from 'blocks/editor/model';
import { selectionLocale } from 'blocks/editor/selection/store';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { EditorMode, editorModeStore } from '../editor_mode';
import { DEFAULT_HOME_SPACE_NAME } from '../model_constants';
import { unrefSection } from '../model_helpers';
import { CommonPanelProps } from '../sidebar/panel_props';
import { PageGenerateForm, PathForm } from './page';
import { SitemapEditSection } from './section_sitemap';
import { WireframeEditSection } from './section_wireframe';
import { SpaceGenerateForm } from './space';

export const EditPanel = observer(function _EditPanel(props: CommonPanelProps) {
  const { selection, navigation, editing, session, analytics, blocksService } =
    props;

  const sel = selection.get();
  const mode = editorModeStore.get();
  const locale = selectionLocale(sel, selection.project);

  if (sel.section) {
    const section = unrefSection(sel.section, selection.project);
    if (!section) {
      return (
        <PanelLayout title="Section" navigation={navigation}>
          <Rows spacing={16}>
            <TextRegular color="red">
              The referenced global section could not be found.
            </TextRegular>
          </Rows>
        </PanelLayout>
      );
    }

    const sectionEditing = editing.forSection(sel.section);
    let Comp = null;
    switch (mode) {
      case EditorMode.Sitemap:
        Comp = SitemapEditSection;
        break;
      case EditorMode.Wireframe:
      case EditorMode.Design:
        Comp = WireframeEditSection;
        break;
      case EditorMode.Styleguide:
        break;
      default:
        throw new UnreachableError(mode);
    }
    return (
      <PanelLayout title="Section" navigation={navigation}>
        {Comp && (
          <Comp
            {...props}
            sel={sel}
            section={section}
            sectionEditing={sectionEditing}
          />
        )}
      </PanelLayout>
    );
  }
  if (sel.page) {
    const pageEditing = editing.forPage(sel.page);
    const isPath = sel.page.pageType === 'path';
    const isRoot = selection.factory.isRootPageOfSpace(sel.page);
    const showPageGroupSwitcher = mode === EditorMode.Sitemap && !isRoot;
    if (isPath) {
      return (
        <PanelLayout title="Page Group" navigation={navigation}>
          <Rows spacing={16}>
            <Button
              label="Change to Page"
              icon={IconPage}
              onClick={() => {
                pageEditing.convertToPage();
                trackGhostPageConversion(analytics, 'page');
              }}
            />
            <PathForm
              {...props}
              locale={locale}
              page={sel.page}
              pageEditing={pageEditing}
            />
          </Rows>
        </PanelLayout>
      );
    }
    return (
      <PanelLayout title="Page" navigation={navigation}>
        <Rows spacing={16}>
          {showPageGroupSwitcher && (
            <PageGroupSwitcher
              analytics={analytics}
              page={sel.page}
              pageEditing={pageEditing}
            />
          )}
          <PageGenerateForm
            {...props}
            locale={locale}
            page={sel.page}
            pageEditing={pageEditing}
          />
        </Rows>
      </PanelLayout>
    );
  }

  const selectedSpace = sel.space || 'home';
  const selectedSpaceName =
    selectedSpace === 'home'
      ? selection.project.homeSpaceName ?? DEFAULT_HOME_SPACE_NAME
      : selectedSpace.name;
  return (
    <PanelLayout title={selectedSpaceName || 'Sitemap'} navigation={navigation}>
      <Rows spacing={16}>
        <SpaceGenerateForm
          {...props}
          blocksService={blocksService}
          space={selectedSpace}
          spaceEditing={editing.forSpace(selectedSpace)}
        />
      </Rows>
    </PanelLayout>
  );
});

type PageGroupSwitcherProps = {
  analytics: AnalyticsService;
  page: Page;
  pageEditing: PageEditingActions;
};

function PageGroupSwitcher({
  analytics,
  page,
  pageEditing,
}: PageGroupSwitcherProps) {
  if (page.pageType === 'path') {
    return (
      <Button
        label="Change to Page"
        icon={IconPage}
        onClick={() => {
          pageEditing.convertToPage();
          trackGhostPageConversion(analytics, 'page');
        }}
      />
    );
  }
  return (
    <Button
      label="Change to Page Group"
      icon={IconPageGroup}
      onClick={() => {
        pageEditing.convertToPath();
        trackGhostPageConversion(analytics, 'ghost');
      }}
    />
  );
}

function trackGhostPageConversion(
  analytics: AnalyticsService,
  to: 'ghost' | 'page',
) {
  analytics.track({
    event: 'blocks/ghost-page-conversion',
    to,
  });
}
